import React, { useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import Box from '@amzn/meridian/box';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type JobTask, setJobFormData } from 'src/store/createNewJobSlice';
import { useAppSelector } from 'src/store/store';

import { generatePlaceholderForRequirementName } from '../../../../utils/requirementFormUtils';

interface TaskRequirementFormProps {
  task: Partial<JobTask>;
  jobFormDataTaskIndex: number;
}

const TaskRequirementForm = ({ task, jobFormDataTaskIndex }: TaskRequirementFormProps) => {
  const dispatch = useDispatch();
  const { jobFormData, requirementFormErrors } = useAppSelector((state) => state.createNewJobPage);

  // allow tabbing between requirement fields without having to tab through body location buttons
  // (workaround for meridian not supporting tabIndex)
  const bodyLocationSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (bodyLocationSectionRef.current !== null) {
      const bodyLocationInputs = bodyLocationSectionRef.current.querySelectorAll('input');
      bodyLocationInputs.forEach((inp) => inp.setAttribute('tabIndex', '-1'));
    }
  }, [bodyLocationSectionRef]);

  const onChange = (newData: Partial<JobTask>) => {
    const newTask = { ...task, ...newData };
    const newTasks = [...jobFormData.tasks];
    newTasks[jobFormDataTaskIndex] = newTask;
    dispatch(setJobFormData({ ...jobFormData, tasks: newTasks }));
  };

  return (
    <Box
      type="outline"
      overflowY="scroll"
      spacingInset={'400'}
      key={task.label}
      role="region"
      aria-labelledby={`task-form-label-${jobFormDataTaskIndex}`}
    >
      <Row>
        <Text type="b200">Task Name</Text>
      </Row>
      <Row>
        <Text type="h100" id={`task-form-label-${jobFormDataTaskIndex}`}>
          {task.label}
        </Text>
      </Row>
      <Row widths={'fill'} spacingInset={'400 none'}>
        <Column spacing={'200'}>
          <Text tag="label" type="b300" color="primary" htmlFor={`task-requirement-input-${jobFormDataTaskIndex}`}>
            Task Requirement
          </Text>
          <Text tag="label" type="b300" color="secondary">
            {task.measureUnit || ''}
          </Text>
          {task.measureUnit !== 'YES/NO' ? (
            <Input
              id={`task-requirement-input-${jobFormDataTaskIndex}`}
              placeholder={generatePlaceholderForRequirementName(task.measureUnit)}
              value={task.requirementName}
              onChange={(requirementName) => onChange({ ...task, requirementName })}
              {...requirementFormErrors[task.label!]?.requirementName}
            />
          ) : (
            <>
              <ButtonGroup
                value={task.requirementName}
                onChange={(requirementName) => {
                  onChange({ ...task, requirementName });
                }}
              >
                <ButtonOption value="Yes">Yes</ButtonOption>
                <ButtonOption value="No">No</ButtonOption>
              </ButtonGroup>
              {requirementFormErrors[task.label!]?.requirementName?.error && (
                <Text color="error" type="b200">
                  {requirementFormErrors[task.label!]?.requirementName?.errorMessage}
                </Text>
              )}
            </>
          )}
        </Column>
      </Row>
      {task.identifyBodyLocation && (
        <Row widths={'fill'}>
          <Column spacing={'200'} ref={bodyLocationSectionRef}>
            <Text tag="label" type="b300" color="primary">
              Identify Body Location
            </Text>
            <ButtonGroup
              value={task.bodyLocationType}
              onChange={(bodyLocationType) => {
                onChange({ ...task, bodyLocationType });
              }}
            >
              <ButtonOption value="L">Left</ButtonOption>
              <ButtonOption value="R">Right</ButtonOption>
              <ButtonOption value="B">Both</ButtonOption>
              <ButtonOption value="LR">L or R</ButtonOption>
            </ButtonGroup>
          </Column>
        </Row>
      )}
    </Box>
  );
};

export default TaskRequirementForm;
